import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import TabNavigation from '../../components/ui/TabNavigation';
import PersonColorLegend from '../../components/ui/PersonColorLegend';
import QuickActionBar from '../../components/ui/QuickActionBar';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import CompletionRateChart from './components/CompletionRateChart';
import SchedulingInsights from './components/SchedulingInsights';
import TaskPerformanceSection from './components/TaskPerformanceSection';
import ExportReportControls from './components/ExportReportControls';
import { format, subMonths, startOfWeek, endOfWeek } from 'date-fns';

const FamilyActivityReports = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [reportType, setReportType] = useState('all');

  // Mock family members data
  const familyMembers = [
    { id: 1, name: 'Sarah', role: 'Mom', color: '#EF4444', initials: 'SM' },
    { id: 2, name: 'Michael', role: 'Dad', color: '#3B82F6', initials: 'MD' },
    { id: 3, name: 'Emma', role: 'Daughter', color: '#EC4899', initials: 'ED' },
    { id: 4, name: 'Lucas', role: 'Son', color: '#10B981', initials: 'LS' },
    { id: 5, name: 'Olivia', role: 'Daughter', color: '#F59E0B', initials: 'OD' },
    { id: 6, name: 'Noah', role: 'Son', color: '#8B5CF6', initials: 'NS' }
  ];

  // Mock tasks data for analytics
  const mockTasks = useMemo(() => [
    { id: 1, personId: 1, title: 'Morning Workout', completed: true, date: '2026-01-06', priority: 'high', duration: 60, category: 'Health' },
    { id: 2, personId: 1, title: 'Prepare Breakfast', completed: true, date: '2026-01-06', priority: 'high', duration: 30, category: 'Household' },
    { id: 3, personId: 2, title: 'School Drop-off', completed: true, date: '2026-01-06', priority: 'high', duration: 30, category: 'Family' },
    { id: 4, personId: 3, title: 'Math Homework', completed: false, date: '2026-01-06', priority: 'high', duration: 45, category: 'Education' },
    { id: 5, personId: 4, title: 'Soccer Practice', completed: true, date: '2026-01-06', priority: 'medium', duration: 90, category: 'Sports' },
    { id: 6, personId: 1, title: 'Grocery Shopping', completed: true, date: '2026-01-05', priority: 'medium', duration: 60, category: 'Household' },
    { id: 7, personId: 2, title: 'Team Meeting', completed: true, date: '2026-01-05', priority: 'high', duration: 120, category: 'Work' },
    { id: 8, personId: 3, title: 'Piano Lesson', completed: true, date: '2026-01-05', priority: 'medium', duration: 60, category: 'Education' },
    { id: 9, personId: 4, title: 'Clean Room', completed: false, date: '2026-01-05', priority: 'low', duration: 30, category: 'Household' },
    { id: 10, personId: 5, title: 'Art Project', completed: true, date: '2026-01-05', priority: 'medium', duration: 45, category: 'Education' },
    { id: 11, personId: 1, title: 'Yoga Session', completed: true, date: '2026-01-04', priority: 'medium', duration: 45, category: 'Health' },
    { id: 12, personId: 2, title: 'Car Maintenance', completed: true, date: '2026-01-04', priority: 'high', duration: 90, category: 'Household' },
    { id: 13, personId: 3, title: 'Science Project', completed: false, date: '2026-01-04', priority: 'high', duration: 120, category: 'Education' },
    { id: 14, personId: 4, title: 'Basketball Game', completed: true, date: '2026-01-04', priority: 'medium', duration: 120, category: 'Sports' },
    { id: 15, personId: 6, title: 'Reading Time', completed: true, date: '2026-01-04', priority: 'low', duration: 30, category: 'Education' },
    { id: 16, personId: 1, title: 'Meal Prep', completed: true, date: '2026-01-03', priority: 'high', duration: 90, category: 'Household' },
    { id: 17, personId: 2, title: 'Client Call', completed: true, date: '2026-01-03', priority: 'high', duration: 60, category: 'Work' },
    { id: 18, personId: 3, title: 'Dance Class', completed: true, date: '2026-01-03', priority: 'medium', duration: 60, category: 'Sports' },
    { id: 19, personId: 5, title: 'Swimming Lesson', completed: true, date: '2026-01-03', priority: 'medium', duration: 60, category: 'Sports' },
    { id: 20, personId: 6, title: 'Homework', completed: false, date: '2026-01-03', priority: 'high', duration: 45, category: 'Education' },
    { id: 21, personId: 1, title: 'Doctor Appointment', completed: true, date: '2026-01-02', priority: 'high', duration: 60, category: 'Health' },
    { id: 22, personId: 2, title: 'Lawn Mowing', completed: true, date: '2026-01-02', priority: 'medium', duration: 45, category: 'Household' },
    { id: 23, personId: 4, title: 'Guitar Practice', completed: true, date: '2026-01-02', priority: 'low', duration: 30, category: 'Education' },
    { id: 24, personId: 5, title: 'Craft Time', completed: true, date: '2026-01-02', priority: 'low', duration: 45, category: 'Education' },
    { id: 25, personId: 1, title: 'Family Dinner Prep', completed: true, date: '2026-01-01', priority: 'high', duration: 60, category: 'Family' },
    { id: 26, personId: 2, title: 'Budget Review', completed: true, date: '2026-01-01', priority: 'medium', duration: 45, category: 'Finance' },
    { id: 27, personId: 3, title: 'Book Club', completed: true, date: '2026-01-01', priority: 'low', duration: 90, category: 'Social' },
    { id: 28, personId: 6, title: 'Video Games', completed: true, date: '2026-01-01', priority: 'low', duration: 60, category: 'Entertainment' }
  ], []);

  // Period options
  const periodOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'Last 3 Months' },
    { value: 'year', label: 'This Year' }
  ];

  // Report type options
  const reportTypeOptions = [
    { value: 'all', label: 'All Activities' },
    { value: 'tasks', label: 'Tasks Only' },
    { value: 'events', label: 'Events Only' },
    { value: 'productivity', label: 'Productivity Focus' }
  ];

  // Member filter options
  const memberOptions = familyMembers?.map(member => ({
    value: member?.id,
    label: member?.name
  }));

  // Filter tasks based on selected period and members
  const filteredTasks = useMemo(() => {
    let filtered = mockTasks;

    // Filter by selected members
    if (selectedMembers?.length > 0) {
      filtered = filtered?.filter(task => selectedMembers?.includes(task?.personId));
    }

    return filtered;
  }, [mockTasks, selectedMembers]);

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const total = filteredTasks?.length;
    const completed = filteredTasks?.filter(t => t?.completed)?.length;
    const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    const overdue = filteredTasks?.filter(t => !t?.completed && new Date(t?.date) < new Date())?.length;

    return { total, completed, completionRate, overdue, pending: total - completed };
  }, [filteredTasks]);

  // Get date range display
  const getDateRangeDisplay = () => {
    const today = new Date();
    switch (selectedPeriod) {
      case 'week':
        return `${format(startOfWeek(today), 'MMM d')} - ${format(endOfWeek(today), 'MMM d, yyyy')}`;
      case 'month':
        return format(today, 'MMMM yyyy');
      case 'quarter':
        return `${format(subMonths(today, 3), 'MMM yyyy')} - ${format(today, 'MMM yyyy')}`;
      case 'year':
        return format(today, 'yyyy');
      default:
        return format(today, 'MMM d, yyyy');
    }
  };

  return (
    <>
      <Helmet>
        <title>Activity Reports - Starling Schedule</title>
        <meta name="description" content="View comprehensive family activity reports, task completion summaries, and scheduling insights with export options" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <TabNavigation />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
          {/* Header Section */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Family Activity Reports
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Comprehensive analytics and insights into family scheduling and task completion
                </p>
              </div>

              <ExportReportControls 
                tasks={filteredTasks}
                familyMembers={familyMembers}
                period={selectedPeriod}
                dateRange={getDateRangeDisplay()}
                stats={overallStats}
              />
            </div>

            {/* Filters */}
            <div className="bg-card rounded-xl border border-border p-4 md:p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  label="Time Period"
                  options={periodOptions}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                  placeholder="Select period"
                />

                <Select
                  label="Family Members"
                  options={memberOptions}
                  value={selectedMembers}
                  onChange={setSelectedMembers}
                  placeholder="All members"
                  multiple
                  searchable
                  clearable
                />

                <Select
                  label="Report Type"
                  options={reportTypeOptions}
                  value={reportType}
                  onChange={setReportType}
                  placeholder="Select type"
                />
              </div>

              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Report Period:</span>
                  <span className="font-medium text-foreground">{getDateRangeDisplay()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 md:mb-8">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 md:p-5 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="list-checks" size={20} className="text-primary" />
                <span className="text-xs md:text-sm text-muted-foreground">Total Tasks</span>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-primary">{overallStats?.total}</div>
            </div>

            <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-xl p-4 md:p-5 border border-success/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="check-circle-2" size={20} className="text-success" />
                <span className="text-xs md:text-sm text-muted-foreground">Completed</span>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-success">{overallStats?.completed}</div>
            </div>

            <div className="bg-gradient-to-br from-warning/10 to-warning/5 rounded-xl p-4 md:p-5 border border-warning/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="clock" size={20} className="text-warning" />
                <span className="text-xs md:text-sm text-muted-foreground">Pending</span>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-warning">{overallStats?.pending}</div>
            </div>

            <div className="bg-gradient-to-br from-error/10 to-error/5 rounded-xl p-4 md:p-5 border border-error/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="alert-circle" size={20} className="text-error" />
                <span className="text-xs md:text-sm text-muted-foreground">Overdue</span>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-error">{overallStats?.overdue}</div>
            </div>
          </div>

          {/* Main Dashboard Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Left Column - Completion Rates */}
            <div className="lg:col-span-2 space-y-6">
              <CompletionRateChart 
                tasks={filteredTasks}
                familyMembers={familyMembers}
                period={selectedPeriod}
              />

              <SchedulingInsights 
                tasks={filteredTasks}
                familyMembers={familyMembers}
              />
            </div>

            {/* Right Column - Task Performance & Legend */}
            <div className="space-y-6">
              <PersonColorLegend layout="vertical" onPersonUpdate={() => {}} />
              
              <TaskPerformanceSection 
                tasks={filteredTasks}
                familyMembers={familyMembers}
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 md:mt-8 bg-card rounded-xl border border-border p-4 md:p-5">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Button variant="outline" iconName="history" fullWidth>
                View Task History
              </Button>
              <Button variant="outline" iconName="trending-up" fullWidth>
                Productivity Trends
              </Button>
              <Button variant="outline" iconName="lightbulb" fullWidth>
                Scheduling Recommendations
              </Button>
            </div>
          </div>
        </main>

        <QuickActionBar onActionSelect={() => {}} />
      </div>
    </>
  );
};

export default FamilyActivityReports;